import React, { useEffect, useRef, useState } from 'react';
import { useCommon } from "../context/CommonContext";
import { useNavigate } from "react-router-dom";
import { useLanguage } from '../context/LanguageProvider';

const IATTestv2a = () => {

    // ---------------------------------------------------
    // CONTEXT HOOKS: Provide shared game and language data
    // ---------------------------------------------------
    const { 
        attributes, 
        currentMemoryIndex, 
        memories, 
        iatTestReaction, 
        intermissionText, 
        kioskTimeoutText, 
        prompts, 
        questionID
    } = useCommon();
    const { translation } = useLanguage();

    // ---------------------------------------------------
    // STATIC WORD LIST SETUP
    // - Use attributes[1] from context if available
    // - Otherwise fall back to a set of default words
    // ---------------------------------------------------
    const WORDS = attributes[1].length === 0 
        ? ["Default-1", "Default-2", "Default-3","Default-4", "Default-5", "Default-6","Default-7", "Default-8", "Default-9"]
        : attributes[1];

    // ---------------------------------------------------
    // GAME CONFIGURATION CONSTANTS
    // ---------------------------------------------------
    const config = {
        minReactionTime: 150,                  // Minimum valid reaction time in ms to filter "too fast" presses
        maxDisplayTime: 1000,                   // How long a word stays visible if no input
        delayBetweenWordsMin: 1000,             // Minimum delay before next word after a response
        delayBetweenWordsMax: 1500,             // Maximum delay before next word
        noReactionTime: 1000,                   // Reaction time recorded if no response
        inactivityTime: 240000,                 // Time without input before showing inactivity warning (4 minutes)
        breakTime: 10000,                        // Break time between game rounds in ms
        gameDelay: 1500,                         // Delay before restarting after break
        maxMissedResponses: Math.floor(WORDS.length / 2), // Threshold to trigger "no responses" warning
        noofGames: 2,                            // Total game rounds
        AditionalInitialWordDelay: 1500          // Extra delay before showing first word
    };

    console.log("--------------component rendered-------------");

    // ---------------------------------------------------
    // REACT STATE VARIABLES (trigger re-render when updated)
    // ---------------------------------------------------
    const [currentWord, setCurrentWord] = useState(""); 
    const [isGameOver, setIsGameOver] = useState(false);
    const [isInactive, setIsInactive] = useState(false);

    // Error screen flags for various conditions
    const [errorOneMessage, setErrorOneMessage] = useState(false);   // No responses for too many words
    const [errorTwoMessage, setErrorTwoMessage] = useState(false);   // Pressed too fast
    const [errorThreeMessage, setErrorThreeMessage] = useState(false); // Pressed when no word is shown

    // Break screen markers
    const [isBreak, setIsBreak] = useState(false);    
    const [isBreak1, setIsBreak1] = useState(false);  

    // Continue game indicators (tied to Space press)
    const [canContinue, setCanContinue] = useState(false);
    const [canContinue3, setCanContinue3] = useState(false);

    // ---------------------------------------------------
    // REFS (mutable, do not trigger re-render)
    // ---------------------------------------------------
    const canContinue2 = useRef(false);               // Tracks if Space should restart game after first break
    const canContinue4 = useRef(false);               // Tracks if Space should start next implicit test after second break
    const finalWordListRef = useRef([]);               // Holds shuffled and processed word order for session
    const wordIndexRef = useRef(0);                    // Tracks current word index
    const startTimeRef = useRef(null);                 // Time word was displayed (used for reaction time)
    const timerRef = useRef(null);                     // Timeout for maxDisplayTime
    const isSpacePressedRef = useRef(false);           // Prevents overly frequent Space presses
    const isPausedRef = useRef(false);                 // Indicates game is paused after an error
    const missedResponsesRef = useRef(0);              // Miss count tracker
    const isTransitioningRef = useRef(false);          // Blocks input during word transitions
    const gamesPlayed = useRef(0);                     // Total game rounds played so far
    const isInitializedRef = useRef(false);            // Ensures initial setup runs only once
    const inactivityTimerRef = useRef(null);           // Timer for inactivity detection
    const displayNextWordTimerRef = useRef(null);      // Timer before next word appears
    const gameEnded = useRef(false);                   // Flag indicating if round finished

    const navigate = useNavigate();

    // ---------------------------------------------------
    // HELPER: Shuffle an array in-place using Fisher–Yates
    // ---------------------------------------------------
    const shuffleArray = (array) => {
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    };

    // ---------------------------------------------------
    // HELPER: Create sequence of words for the game
    // Adds "burnout" words early, then rest of list
    // ---------------------------------------------------
    const createFinalWordList = () => {
        const shuffledWords = shuffleArray([...WORDS]);
        const burnoutCount = 2;
        const randomIndex = Math.random() < 0.5 ? 3 : 4; // Random position to slice for burnout pool
        const slicedWords = shuffledWords.slice(randomIndex);
        const burnoutWords = getRandomWords(slicedWords, burnoutCount);
        const finalList = [...burnoutWords, ...shuffledWords];
        finalWordListRef.current = finalList;
        console.log(`finalword list - ${finalWordListRef.current}`);
    };

    // ---------------------------------------------------
    // HELPER: Pick a specified number of random unique items from an array
    // ---------------------------------------------------
    const getRandomWords = (array, count) => {
        const result = [];
        const seenIndices = new Set();
        while (result.length < count) {
            const randomIndex = Math.floor(Math.random() * array.length);
            if (!seenIndices.has(randomIndex)) {
                seenIndices.add(randomIndex);
                result.push(array[randomIndex]);
            }
        }
        return result;
    };

    // ---------------------------------------------------
    // MAIN EFFECT: Run once when component mounts
    // Sets first word and starts input listeners
    // ---------------------------------------------------
    useEffect(() => {
        console.log("--------------useEffect called-------------");
        let timeoutId; // used for initial delay

        // Function to set initial word after random delay
        const setCurrentWordWithDelay = () => {
            return new Promise((resolve) => {
                const initialDelay = (
                    Math.random() * (config.delayBetweenWordsMax - config.delayBetweenWordsMin) 
                    + config.delayBetweenWordsMin 
                    + config.AditionalInitialWordDelay
                );
                timeoutId = setTimeout(() => {
                    setCurrentWord(finalWordListRef.current[0]);
                    resolve();
                }, initialDelay);
            });
        };

        // Game initialization routine
        const initializeGame = async () => {
            if (!isInitializedRef.current) {
                createFinalWordList();
                await setCurrentWordWithDelay();
                isInitializedRef.current = true;
            }
            missedResponsesRef.current = 0;

            // Set up event listeners
            window.addEventListener('keydown', handleKeyPress);
            window.addEventListener('mousemove', resetInactivityTimer);
            resetInactivityTimer();
                        console.log('Game started');

            // Start clock for reaction timing of first word
            startTimeRef.current = performance.now();
            console.log(`Displaying word: ${finalWordListRef.current[wordIndexRef.current]}`);

            // Schedule timeout for missed reaction if maxDisplayTime expires
            timerRef.current = setTimeout(() => {
                console.log(`No reaction for word: ${finalWordListRef.current[wordIndexRef.current]}`);
                isTransitioningRef.current = true;
                missedResponsesRef.current += 1;

                // Log a "no reaction" result
                iatTestReaction.current.push([
                    finalWordListRef.current[wordIndexRef.current],
                    config.noReactionTime,
                    memories[currentMemoryIndex.current],
                    gamesPlayed.current,
                    wordIndexRef.current,
                    false, // user did not respond
                    currentMemoryIndex.current + 1,
                    questionID[1],
                ]);

                // Wait random delay before showing next word
                const randomDelay = (
                    Math.random() * (config.delayBetweenWordsMax - config.delayBetweenWordsMin) 
                    + config.delayBetweenWordsMin
                );
                console.log(`random delay ${randomDelay}`);
                setCurrentWord("");
                displayNextWordTimerRef.current = setTimeout(() => {
                    displayNextWord();
                }, randomDelay);

            }, config.maxDisplayTime);
        };

        initializeGame();

        // Remove timers and listeners on cleanup
        return () => {
            window.removeEventListener('keydown', handleKeyPress);
            window.removeEventListener('mousemove', resetInactivityTimer);
            clearTimeout(timerRef.current);
            clearTimeout(inactivityTimerRef.current);
            clearTimeout(timeoutId);
        };

    }, []); // Only run on mount

    // ---------------------------------------------------
    // Reset inactivity timer (4 min until idle warning)
    // ---------------------------------------------------
    const resetInactivityTimer = () => {
        setIsInactive(false);
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = setTimeout(() => {
            setIsInactive(true);
        }, config.inactivityTime);
    };

    // ---------------------------------------------------
    // Display the next word in the list, handle end-of-game
    // ---------------------------------------------------
    const displayNextWord = () => {
        console.log("--------------displayNextWord called-------------");

        const nextIndex = wordIndexRef.current + 1;

        // End game if all words were shown
        if (nextIndex >= finalWordListRef.current.length) {
            setIsGameOver(true);
            gameEnded.current = true;
            console.log('Game Over. Reaction Times:', iatTestReaction.current);
            handleGameOver();
            return;
        }

        // Move to next word
        wordIndexRef.current = nextIndex;
        setCurrentWord(finalWordListRef.current[nextIndex]);
        isTransitioningRef.current = false;
        isSpacePressedRef.current = false;
        startTimeRef.current = performance.now();

        console.log(`Displaying word: ${finalWordListRef.current[wordIndexRef.current]}`);

        // Set timeout if user doesn't respond in time
        timerRef.current = setTimeout(() => {
            console.log(`No reaction for word: ${finalWordListRef.current[wordIndexRef.current]}`);
            setCurrentWord("");
            missedResponsesRef.current += 1;
            if (missedResponsesRef.current > config.maxMissedResponses) {
                // Too many missed responses — pause and show error
                const currentWord = finalWordListRef.current[wordIndexRef.current];
                iatTestReaction.current.push([
                    currentWord, 
                    config.noReactionTime,
                    memories[currentMemoryIndex.current],
                    gamesPlayed.current,
                    wordIndexRef.current,
                    false,
                    currentMemoryIndex.current + 1,
                    questionID[1],
                ]);
                isPausedRef.current = true;
                isSpacePressedRef.current = false;
                setErrorOneMessage(true);
            } else {
                // Normal missed reaction: log and move forward
                isTransitioningRef.current = true;
                iatTestReaction.current.push([
                    finalWordListRef.current[wordIndexRef.current], 
                    config.noReactionTime,
                    memories[currentMemoryIndex.current],
                    gamesPlayed.current,
                    wordIndexRef.current,
                    false, 
                    currentMemoryIndex.current + 1,
                    questionID[1],
                ]);
                const randomDelay = (
                    Math.random() * (config.delayBetweenWordsMax - config.delayBetweenWordsMin) 
                    + config.delayBetweenWordsMin
                );
                console.log(`random delay ${randomDelay}`);
                displayNextWordTimerRef.current = setTimeout(() => {
                    displayNextWord();
                }, randomDelay);
            }
        }, config.maxDisplayTime);
    };

    // ---------------------------------------------------
    // Handle end-of-round behavior, breaks, navigation
    // ---------------------------------------------------
    const handleGameOver = () => {
        gamesPlayed.current += 1;
        if (gamesPlayed.current < config.noofGames) {
            // End of first round
            setIsBreak(true);
            isTransitioningRef.current = true;
            gameEnded.current = true;
            console.log("First game over. Starting break before the second game...");
            setTimeout(() => {
                                // console.log("Starting second game...");
                // resetGame();
                setCanContinue(true);
                canContinue2.current = true;
            }, config.breakTime);
        } else {
            // End of second round
            console.log("Second game over. Navigating to thank you page...");
            if (attributes.length > 2) {
                setIsGameOver(true);
                setIsBreak1(true);
                canContinue4.current = true;
                setTimeout(() => {
                    setCanContinue3(true);
                }, config.breakTime);
            } else {
                navigate('/IATTestThankYou');
            }
        }
    };

    // ---------------------------------------------------
    // Reset game state for new round
    // ---------------------------------------------------
    const resetGame = async () => {
        console.log("Resetting game for the next round...");
        setIsGameOver(false);
        
        // Random delay before next start
        const randomDelay = (
            Math.random() * (config.delayBetweenWordsMax - config.delayBetweenWordsMin) 
            + config.delayBetweenWordsMin 
            + config.AditionalInitialWordDelay
        );
        await new Promise((resolve) => setTimeout(resolve, randomDelay));

        // Reset trackers
        isPausedRef.current = false;
        isSpacePressedRef.current = false;
        wordIndexRef.current = 0;
        missedResponsesRef.current = 0;
        createFinalWordList();
        isTransitioningRef.current = false;
        gameEnded.current = false;

        // Start first word
        setCurrentWord(finalWordListRef.current[0]);
        startTimeRef.current = performance.now();

        // Timeout in case of no reaction to first word
        timerRef.current = setTimeout(() => {
            console.log(`No reaction for word: ${finalWordListRef.current[wordIndexRef.current]}`);
            isTransitioningRef.current = true;
            missedResponsesRef.current += 1;
            iatTestReaction.current.push([
                finalWordListRef.current[wordIndexRef.current], 
                config.noReactionTime,
                memories[currentMemoryIndex.current],
                gamesPlayed.current,
                wordIndexRef.current,
                false, 
                currentMemoryIndex.current + 1,
                questionID[1],
            ]);
            const randomDelay = (
                Math.random() * (config.delayBetweenWordsMax - config.delayBetweenWordsMin) 
                + config.delayBetweenWordsMin
            );
            console.log(`random delay ${randomDelay}`);
            setCurrentWord("");
            displayNextWordTimerRef.current = setTimeout(() => {
                displayNextWord();
            }, randomDelay);
        }, config.maxDisplayTime);
    };

    // ---------------------------------------------------
    // KEYBOARD INPUT HANDLER (Spacebar-driven game control)
    // ---------------------------------------------------
    const handleKeyPress = (event) => {
        console.log("--------------handleKeyPress called-------------");
        resetInactivityTimer();

        // Space to resume after first round's break
        if (event.code === 'Space' && canContinue2.current) {
            canContinue2.current = false;
            setCanContinue(false);
            setIsBreak(false);
            setTimeout(() => {
                resetGame();
            }, config.gameDelay);
            return;
        }

        // Space to proceed after second break
        if (event.code === 'Space' && canContinue4.current) {
            navigate('/IATTestv2b');
        }

        if (event.code === 'Space' && !isSpacePressedRef.current && !isGameOver && !isTransitioningRef.current) {
            isSpacePressedRef.current = true;
            console.log("inside")
            // Resume from paused error states
            if (isPausedRef.current) {
                console.log("Resuming game...");
                setErrorOneMessage(false);
                setErrorTwoMessage(false);
                setErrorThreeMessage(false);
                missedResponsesRef.current = 0;
                isPausedRef.current = false;
                const randomDelay = (
                    Math.random() * (config.delayBetweenWordsMax - config.delayBetweenWordsMin) 
                    + config.delayBetweenWordsMin
                );
                console.log(`random delay ${randomDelay}`);
                setCurrentWord("");
                displayNextWordTimerRef.current = setTimeout(() => {
                    displayNextWord();
                }, randomDelay);
                return;
            }

            // Log a valid reaction
            clearTimeout(timerRef.current);
            const reactionTime = performance.now() - startTimeRef.current;
            const roundedReactionTime = Math.round(reactionTime * 100) / 100;

            // Too-fast reaction case
            if (reactionTime < config.minReactionTime) {
                console.log(`Reaction time too fast: ${reactionTime}ms. Pausing game.`);
                setCurrentWord("");
                isPausedRef.current = true;
                missedResponsesRef.current = 0;
                isSpacePressedRef.current = false;
                setErrorTwoMessage(true);
                const currentWord = finalWordListRef.current[wordIndexRef.current];
                iatTestReaction.current.push([ 
                    currentWord, 
                    roundedReactionTime,
                    memories[currentMemoryIndex.current],
                    gamesPlayed.current,
                    wordIndexRef.current,
                    true, // fast but invalid
                    currentMemoryIndex.current + 1,
                    questionID[1],
                ]);
                return;
            }

            // Valid reaction logging
            const currentWord = finalWordListRef.current[wordIndexRef.current];
            iatTestReaction.current.push([
                currentWord, 
                roundedReactionTime,
                memories[currentMemoryIndex.current],
                gamesPlayed.current,
                wordIndexRef.current,
                true, 
                currentMemoryIndex.current + 1,
                questionID[1],
            ]);
            missedResponsesRef.current = 0;
                        console.log(`Reaction time for ${currentWord}: ${reactionTime}ms`);

            const randomDelay = (
                Math.random() * (config.delayBetweenWordsMax - config.delayBetweenWordsMin) 
                + config.delayBetweenWordsMin
            );
            console.log(`random delay ${randomDelay}`);
            isTransitioningRef.current = true;
            setCurrentWord("");
            displayNextWordTimerRef.current = setTimeout(() => {
                displayNextWord();
            }, randomDelay);

        } else {
            // Case: Space pressed during transition — warn user
            if (!gameEnded.current && event.code === 'Space') {
                console.log("pressed Space while transitioning between words");
                clearTimeout(displayNextWordTimerRef.current);
                clearTimeout(timerRef.current);
                setCurrentWord("");
                isPausedRef.current = true;
                missedResponsesRef.current = 0;
                isSpacePressedRef.current = false;
                isTransitioningRef.current = false;
                setErrorThreeMessage(true);
            }
        }
    };

    // ---------------------------------------------------
    // RENDER UI LAYOUT
    // ---------------------------------------------------
    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 relative">
            {isGameOver ? (
                // End-of-round screens
                <div className="flex flex-col items-center justify-center">
                    {isBreak && <h2 className="text-4xl font-semibold mb-4">{translation("IAT_IntermissionText")}</h2>}
                    {isBreak1 && <h2 className="text-4xl font-semibold mb-4">{translation("IAT_NextImplicitMessage")}</h2>}
                    {canContinue && <h2 className="absolute bottom-20 text-4xl text-gray-800 mt-4">{translation("IAT_PressSpaceToContinue")}</h2>}
                    {canContinue3 && <h2 className="absolute bottom-20 text-4xl text-gray-800 mt-4">{translation("IAT_PressSpaceToContinue")}</h2>}
                    {/* <h2 className="text-xl font-semibold mb-4">This text and below details are currently used for testing purpose, will be removed in the final version</h2>
                    <h2>Reaction Times:</h2>
                    <ul>
                    {iatTestReaction.current.map((result, index) => (
                        <li key={index}>
                            Word: {result[0]} <br />
                            Reaction Time: { `${result[1]}ms`} <br />
                            Memory: {result[2]} <br />
                            Games Played: {result[3]} <br />
                            Current Index: {result[4]} <br />
                            User Reacted: {result[5]}
                        </li>
                    ))}
                    </ul> */}
                </div>
            ) : ( 
                // Main game UI
                <div>
                    <div className="fixed inset-0 flex flex-col items-center justify-center">
                        {!errorOneMessage && !errorTwoMessage && !errorThreeMessage && (
                            <h2 className="absolute transform -translate-y-16 text-2xl font-semibold text-gray-500">
                                {prompts[1]}
                            </h2>
                        )}
                        <h2 className="absolute top-1/2 transform -translate-y-1/2 text-6xl">
                            {currentWord}
                        </h2>
                    </div>

                    {/* Error messages */}
                    {errorOneMessage && (
                        <div className="text-red-600 mb-8 text-left font-bold left-0 text-3xl">
                            {translation("IAT_NoResponsesDetected")}
                            <h6 className="text-2xl text-gray-700 mb-4 mt-4 text-left w-full max-w-5xl">
                                {translation("IAT_NoResponsesMessage")}
                                <br/> {translation("IAT_RememberToPressSpace")}
                                <br/> {translation("IAT_ResponsesMonitored")}
                                <br/> {translation("IAT_PleasePressSpaceToContinue")}
                            </h6>
                        </div>
                    )}

                    {errorTwoMessage && (
                        <div className="text-red-600 mb-8 text-left font-bold left-0 text-3xl">
                            {translation("IAT_TooFastWarning")}
                            <h6 className="text-2xl text-gray-700 mb-4 mt-4 text-left w-full max-w-5xl">
                                {translation("IAT_TooFastMessage")}
                                <br/> {translation("IAT_ConsiderEachWord")}
                                <br/> {translation("IAT_ResponsesMonitoredFocus")}
                                <br/> {translation("IAT_PleasePressSpaceToContinue")}
                            </h6>
                        </div>
                    )}

                    {errorThreeMessage && (
                        <div className="text-red-600 mb-8 text-left font-bold left-0 text-3xl">
                            {translation("IAT_NoWordOnScreenWarning")}
                            <h6 className="text-2xl text-gray-700 mb-4 mt-4 text-left w-full max-w-5xl">
                                {translation("IAT_NoWordMessage")}
                                <br/> {translation("IAT_RespondWhenWordAppears")}
                                <br/> {translation("IAT_ResponsesMonitoredFocusAgain")}
                                <br/> {translation("IAT_PleasePressSpaceToContinue")}
                            </h6>
                        </div>
                    )}
                </div>
            )}

            {/* Inactivity warning overlay */}
            {isInactive && (
                <div className="fixed inset-0 flex items-center justify-center bg-yellow-200">
                    <p className="text-5xl font-bold">{kioskTimeoutText.current}</p>
                </div>
            )}
        </div>
    );
};

export default IATTestv2a;